import * as types from '../mutation-types'
import fairs from '@/api/fair'
import Vue from 'vue'

const state = {
  fairs: {},
  eligibleFairs: {},
  fairTemplates: [],
  fairSubmissionLoader: false
}

const getters = {
  fairs: (state) => {
    return state.fairs
  },
  eligibleFairs: (state) => {
    return state.eligibleFairs
  },
  currentFair: (state) => (currentPage) => {
    return state.fairs[currentPage]
  },
  fairTemplates: (state) => {
    return state.fairTemplates
  },
  getFairSubmissionLoading: (state) => {
    return state.fairSubmissionLoader
  },
  fairTemplateOpenDate: (state) => (id) => {
    const template = state.fairTemplates ? state.fairTemplates.find(template => template.templateId === id) : null
    return template?.submissionOpen
  },
  fairTemplateDeadline: (state) => (id) => {
    const template = state.fairTemplates ? state.fairTemplates.find(template => template.templateId === id) : null
    return template?.submissionDeadline ? template?.submissionDeadline : template?.submissionDeadline
  },
  fairTemplateDisplayDeadline: (state) => (id) => {
    const template = state.fairTemplates ? state.fairTemplates.find(template => template.templateId === id) : null
    return template?.displaySubmissionDeadline ? template?.displaySubmissionDeadline : template?.displaySubmissionDeadline
  },
  fairTemplateCanSubmit: (state) => (id) => {
    const template = state.fairTemplates ? state.fairTemplates.find(template => template.templateId === id) : null
    return template?.canSubmit
  },
  isWorkspaceProject: (state) => (id) => {
    const template = state.fairTemplates ? state.fairTemplates.find(template => template.templateId === id) : null
    return template ? !template.canSubmit : false
  },
  templateFairId: (state) => (id) => {
    const template = state.fairTemplates ? state.fairTemplates.find(template => template.templateId === id) : null
    return template?.fairId
  },
  checkFairSubmissionOpenDates: function () {
    let startDate = null
    if (state.fairTemplates.length > 0) {
      state.fairTemplates.forEach((template) => {
        if (template.submissionOpen) {
          const templateOpenDate = new Date(template.submissionOpen)
          if (templateOpenDate < startDate || !startDate) {
            startDate = templateOpenDate
          }
        }
      })
    }
    return startDate
  },
  checkFairSubmissionCloseDates: function () {
    let closeDate = null

    if (state.fairTemplates.length > 0) {
      state.fairTemplates.forEach((template) => {
        if (template.submissionDeadline) {
          const templateCloseDate = new Date(template.submissionDeadline
          )

          if (templateCloseDate > closeDate || !closeDate) {
            closeDate = templateCloseDate
          }
        }
      })
    }

    return closeDate
  },
  fairForCategory: state => category => {
    return state.fairs[category]
  },
  fairForTemplate: state => templateId => {
    return state.fairs[templateId]
  }
}

const actions = {
  getFairsForProject ({ commit, state }, projectID) {
    return new Promise((resolve, reject) => {
      fairs.getFairsForProject(projectID).then((response) => {
        const fairs = response.data.result.fairs
        fairs.forEach((fair) => {
          commit(types.ADD_FAIR, fair)
          commit(types.ADD_PROJECT_FAIR, { projectID, fair })
        })
        commit(types.ELIGIBLE_FAIRS, fairs)
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  createFairProject ({ commit, state }, { projectID, data }) {
    return new Promise((resolve, reject) => {
      fairs.createFairProject(projectID, data).then((response) => {
        commit(types.REMOVE_FROM_ELIGIBLE_FAIRS, data.fairs)
        const success = response.data.result.success
        resolve(success)
      }, (error) => {
        reject(error)
      })
    })
  },
  clearEligibleFairs ({ commit, state }) {
    commit(types.CLEAR_FAIRS)
  },
  getFairForEvent ({ commit, state }, explorePath) {
    return new Promise((resolve, reject) => {
      fairs.getFairForEvent(explorePath).then((response) => {
        if (response.status === 200) {
          const fair = response.data.result.fair
          commit(types.ADD_EVENT_FAIR, fair)
          resolve(fair)
        } else {
          reject(new Error('Error getting fair for event'))
        }
      }, (error) => {
        reject(error)
      })
    })
  },
  getFairForCategory ({ commit, state }, category) {
    return new Promise((resolve, reject) => {
      fairs.getFairForCategory(category).then((response) => {
        const fair = response.data.result.fair
        commit(types.ADD_CATEGORY_FAIR, { category, fair })
        resolve(fair)
      }, (error) => {
        reject(error)
      })
    })
  },
  getFairForTemplate ({ commit, state }, templateId) {
    return new Promise((resolve, reject) => {
      fairs.getFairForTemplate(templateId).then((response) => {
        const fair = response.data.result.fair
        commit(types.ADD_TEMPLATE_FAIR, { templateId, fair })
        resolve(fair)
      }, (error) => {
        reject(error)
      })
    })
  },
  getTemplatesForFair ({ commit, state }) {
    return new Promise((resolve, reject) => {
      fairs.getTemplatesForFair().then((response) => {
        const fairTemplates = response.data.result.templates
        commit(types.SET_FAIR_TEMPLATES, fairTemplates)
        resolve(fairTemplates)
      }, (error) => {
        reject(error)
      })
    })
  },
  setSubmissionLoading ({ commit }, value) {
    commit(types.SET_FAIR_SUBMISSION_LOADING, value)
  }
}

const mutations = {
  [types.ADD_FAIR] (state, fair) {
    Vue.set(state.fairs, fair.abbreviation, fair)
  },
  [types.ADD_EVENT_FAIR] (state, fair) {
    Vue.set(state.fairs, fair.explore_path, fair)
  },
  [types.ADD_CATEGORY_FAIR] (state, { category, fair }) {
    Vue.set(state.fairs, category, fair)
  },
  [types.ADD_TEMPLATE_FAIR] (state, { templateId, fair }) {
    Vue.set(state.fairs, templateId, fair)
  },
  [types.ADD_PROJECT_FAIR] (state, { projectID, fair }) {
    Vue.set(state.fairs, projectID, fair)
  },
  [types.ELIGIBLE_FAIRS] (state, fairs) {
    state.eligibleFairs = fairs
  },
  [types.REMOVE_FROM_ELIGIBLE_FAIRS] (state, fairs) {
    // 'fairs' is an array of a single or multiple fairIDs
    // use filter to remove said fair(s)
    state.eligibleFairs = state.eligibleFairs.filter((eligibleFair) => {
      return !fairs.includes(eligibleFair.id)
    })
  },
  [types.CLEAR_FAIRS] (state) {
    state.eligibleFairs = {}
    state.fairs = {}
  },
  [types.SET_FAIR_TEMPLATES] (state, fairTemplates) {
    state.fairTemplates = fairTemplates
  },
  [types.SET_FAIR_SUBMISSION_LOADING] (state, value) {
    state.fairSubmissionLoader = value
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
